// src/components/ScoreProgress.jsx
import React, { useEffect, useRef } from 'react';
import { TrendingUp, TrendingDown, Minus, AlertCircle, CheckCircle, Zap } from 'lucide-react';
import { useIsMobile } from '../hooks/useMediaQuery'; // 👈 NUEVO IMPORT

/**
 * 🎯 Componente ScoreProgress - Barra de progresso profissional
 * 
 * Funcionalidades:
 * - 📊 Barra de progresso animada
 * - 🎨 Cores dinâmicas baseadas no score
 * - 💡 Ícone indicador de tendência
 * - 🔥 Mensagem contextual
 * - 🖱️ Hover com tooltip explicativo
 * - ♿ Acessível (ARIA labels)
 * - 📱 Adaptado para dispositivos móviles
 */

const getScoreConfig = (score) => {
  const numScore = Number(score) || 0;
  
  if (numScore >= 80) {
    return {
      color: 'from-emerald-500 to-green-500',
      icon: <Zap className="w-4 h-4 text-emerald-600" />,
      message: '🔥 Pronto para fechar!',
      shortMessage: '🔥 Fechar',
      trend: 'up',
      bgLight: 'bg-emerald-50',
      textColor: 'text-emerald-700',
      borderColor: 'border-emerald-200',
      aria: 'Alta probabilidade de fechamento'
    };
  } else if (numScore >= 50) {
    return {
      color: 'from-amber-500 to-orange-500',
      icon: <TrendingUp className="w-4 h-4 text-amber-600" />,
      message: '📈 Aquecendo, continue assim!',
      shortMessage: '📈 Aquecendo',
      trend: 'up',
      bgLight: 'bg-amber-50',
      textColor: 'text-amber-700',
      borderColor: 'border-amber-200',
      aria: 'Média probabilidade de fechamento'
    };
  } else if (numScore >= 25) {
    return {
      color: 'from-blue-500 to-indigo-500',
      icon: <Minus className="w-4 h-4 text-blue-600" />,
      message: '🔄 Nutrir relacionamento',
      shortMessage: '🔄 Nutrir',
      trend: 'stable',
      bgLight: 'bg-blue-50',
      textColor: 'text-blue-700',
      borderColor: 'border-blue-200',
      aria: 'Baixa probabilidade de fechamento'
    };
  } else {
    return {
      color: 'from-slate-500 to-gray-500',
      icon: <TrendingDown className="w-4 h-4 text-slate-600" />,
      message: '🎯 Qualificar melhor este lead',
      shortMessage: '🎯 Qualificar',
      trend: 'down',
      bgLight: 'bg-slate-50',
      textColor: 'text-slate-700',
      borderColor: 'border-slate-200',
      aria: 'Muito baixa probabilidade de fechamento'
    };
  }
};

const getScoreMessage = (score) => {
  const numScore = Number(score) || 0;
  
  if (numScore >= 80) return 'Cliente com alto potencial de compra. Prioridade máxima!';
  if (numScore >= 50) return 'Cliente demonstra interesse, continue acompanhando.';
  if (numScore >= 25) return 'Cliente em fase inicial, precisa ser nutrido.';
  return 'Lead frio, qualificar melhor antes de investir tempo.';
};

export const ScoreProgress = ({ 
  score = 0, 
  size = 'md', // sm, md, lg
  showMessage = true,
  showIcon = true,
  animated = true,
  className = '',
  onHover,
  onClick
}) => {
  const progressRef = useRef(null);
  const isMobile = useIsMobile(); // 👈 DETECTAR MÓVIL
  const config = getScoreConfig(score);
  const numericScore = Math.min(100, Math.max(0, Number(score) || 0));
  
  // Animar barra de progresso na montagem
  useEffect(() => {
    if (progressRef.current && animated) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              progressRef.current.style.width = `${numericScore}%`;
            }
          });
        },
        { threshold: 0.1 }
      );
      
      observer.observe(progressRef.current);
      return () => observer.disconnect();
    }
  }, [numericScore, animated]);

  // Tamaños responsivos - ADAPTADOS
  const sizes = {
    sm: {
      bar: 'h-1.5',
      text: isMobile ? 'text-[9px]' : 'text-xs',
      icon: isMobile ? 'w-3 h-3' : 'w-3.5 h-3.5',
      padding: isMobile ? 'p-1.5' : 'p-2'
    },
    md: {
      bar: 'h-2.5',
      text: isMobile ? 'text-[10px]' : 'text-sm',
      icon: isMobile ? 'w-3.5 h-3.5' : 'w-4 h-4',
      padding: isMobile ? 'p-2' : 'p-3'
    },
    lg: {
      bar: 'h-4',
      text: isMobile ? 'text-xs' : 'text-base',
      icon: isMobile ? 'w-4 h-4' : 'w-5 h-5',
      padding: isMobile ? 'p-3' : 'p-4'
    }
  };

  const currentSize = sizes[size] || sizes.md;

  const handleClick = (e) => {
    if (onClick) {
      onClick(e, { score: numericScore, config });
    }
  };

  const handleMouseEnter = () => {
    if (onHover && !isMobile) { // 👈 Solo hover en desktop
      onHover({ score: numericScore, config });
    }
  };

  return (
    <div 
      className={`w-full ${className}`}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      role="region"
      aria-label={`Score de probabilidade: ${numericScore}%`}
    >
      
      {/* Header con score e ícone - ADAPTADO */}
      <div className={`flex items-center justify-between ${isMobile ? 'mb-1' : 'mb-2'}`}>
        <div className="flex items-center gap-1">
          {showIcon && (
            <div className={`${config.bgLight} rounded-lg ${currentSize.padding}`}>
              {config.icon}
            </div>
          )}
          <div>
            {!isMobile && (
              <span className={`text-[10px] font-medium ${config.textColor} uppercase tracking-wider`}>
                Score de Probabilidade
              </span>
            )}
            <div className="flex items-center gap-1">
              <span className={`font-bold ${config.textColor} ${isMobile ? 'text-base' : 'text-2xl'}`}>
                {numericScore}%
              </span>
              {showMessage && !isMobile && (
                <span className={`${currentSize.text} ${config.textColor} opacity-75`}>
                  {config.message}
                </span>
              )}
            </div>
          </div>
        </div>
        
        {/* Badge de tendência - ADAPTADO */}
        <div 
          className={`${config.bgLight} ${config.textColor} rounded-full font-medium flex items-center gap-1 ${
            isMobile ? 'px-1.5 py-0.5 text-[8px]' : 'px-2 py-1 text-xs'
          }`}
          role="status"
          aria-label={`Tendência: ${config.trend === 'up' ? 'positiva' : config.trend === 'down' ? 'negativa' : 'estável'}`}
        >
          {config.trend === 'up' && <TrendingUp className={isMobile ? 'w-2 h-2' : 'w-3 h-3'} />}
          {config.trend === 'down' && <TrendingDown className={isMobile ? 'w-2 h-2' : 'w-3 h-3'} />}
          {config.trend === 'stable' && <Minus className={isMobile ? 'w-2 h-2' : 'w-3 h-3'} />}
          <span className={isMobile ? 'sr-only' : ''}>
            {config.trend === 'up' ? 'Alta' : config.trend === 'down' ? 'Baixa' : 'Estável'}
          </span>
        </div>
      </div>

      {/* Barra de progresso principal */}
      <div 
        className={`w-full ${config.bgLight} rounded-full overflow-hidden ${currentSize.bar} relative group`}
        role="progressbar"
        aria-valuenow={numericScore}
        aria-valuemin="0"
        aria-valuemax="100"
        aria-valuetext={`${numericScore}% - ${config.aria}`}
      >
        {/* Barra animada */}
        <div
          ref={progressRef}
          className={`h-full bg-gradient-to-r ${config.color} rounded-full transition-all duration-1000 ease-out relative`}
          style={{ width: animated ? '0%' : `${numericScore}%` }}
        >
          {/* Efeito de brilho no hover - solo desktop */}
          {!isMobile && (
            <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity" />
          )}
        </div>

        {/* Tooltip personalizado - solo desktop */}
        {!isMobile && (
          <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-slate-800 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
            {numericScore}% - {config.message}
            <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-slate-800 rotate-45" />
          </div>
        )}
      </div>

      {/* Mensagem detalhada - ADAPTADA */}
      {showMessage && size !== 'sm' && (
        <div className={`mt-2 flex items-start gap-1 ${config.bgLight} ${config.textColor} rounded-xl border ${config.borderColor} ${
          isMobile ? 'p-2' : currentSize.padding
        }`}>
          {config.trend === 'up' && <CheckCircle className={`${isMobile ? 'w-3 h-3' : currentSize.icon} flex-shrink-0 mt-0.5`} />}
          {config.trend === 'down' && <AlertCircle className={`${isMobile ? 'w-3 h-3' : currentSize.icon} flex-shrink-0 mt-0.5`} />}
          {config.trend === 'stable' && <Minus className={`${isMobile ? 'w-3 h-3' : currentSize.icon} flex-shrink-0 mt-0.5`} />}
          <p className={`${isMobile ? 'text-[10px]' : currentSize.text} leading-relaxed`}>
            {isMobile ? config.shortMessage : getScoreMessage(numericScore)}
          </p>
        </div>
      )}

      {/* Micro-interação: pontos de referência - solo desktop */}
      {!isMobile && (
        <div className="flex justify-between mt-1 px-1">
          <span className="text-[10px] text-slate-400">0%</span>
          <span className="text-[10px] text-slate-400">25%</span>
          <span className="text-[10px] text-slate-400">50%</span>
          <span className="text-[10px] text-slate-400">75%</span>
          <span className="text-[10px] text-slate-400">100%</span>
        </div>
      )}
    </div>
  );
};

// Versão simplificada para uso rápido - ADAPTADA
ScoreProgress.Simple = ({ score, className }) => {
  const isMobile = useIsMobile();
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <div className="flex-1">
        <div className="w-full bg-slate-100 rounded-full overflow-hidden">
          <div className={`${isMobile ? 'h-1.5' : 'h-2'}`}>
            <div 
              className="h-full bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full transition-all duration-500"
              style={{ width: `${Math.min(100, Math.max(0, Number(score) || 0))}%` }}
            />
          </div>
        </div>
      </div>
      <span className={`font-medium text-slate-700 ${isMobile ? 'text-xs' : 'text-sm'}`}>{score}%</span>
    </div>
  );
};

// Versão com cores do sistema (white label) - ADAPTADA
ScoreProgress.Themed = ({ score, primaryColor = '#2563eb', className }) => {
  const isMobile = useIsMobile();
  return (
    <div className={`space-y-1 ${className}`}>
      <div className={`flex justify-between ${isMobile ? 'text-xs' : 'text-sm'}`}>
        <span className="text-slate-600">{isMobile ? 'Chance' : 'Chance de fechamento'}</span>
        <span className="font-bold" style={{ color: primaryColor }}>
          {score}%
        </span>
      </div>
      <div className="w-full bg-slate-100 rounded-full overflow-hidden">
        <div className={`${isMobile ? 'h-1.5' : 'h-2'}`}>
          <div 
            className="h-full rounded-full transition-all duration-500"
            style={{ 
              width: `${Math.min(100, Math.max(0, Number(score) || 0))}%`,
              background: `linear-gradient(90deg, ${primaryColor}, ${primaryColor}dd)`
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default ScoreProgress;